import { format } from 'date-fns';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { toLocalDateFromMs } from '../../common/dateTimeHelper';
import { formatInteger } from '../../common/formatHelper';
import useFormatter from '../../common/useFormatter';
import * as Icon from '../../components/icons';
import { actualityExtraMetricDescriptors } from '../../feed/store/actuality.heatmap.state';
import useItemSetMetricDescriptorsMap from '../itemSet/useItemSetMetricDescriptors';
import useItemStatsMetricDescriptorsMap from '../itemStats/useItemStatsMetricDescriptors';
import useLocationMetricDescriptorsMap from '../location/useLocationMetricDescriptors';
import { actualityMetricDescriptorsMap } from './actualityMetric.default';
import { ActualityMetricDescriptor } from './actualityMetric.types';

function useActualityMetricDescriptorsMap() {
  const { t } = useTranslation('feed');
  const formatter = useFormatter();
  const extraDescriptors = useRecoilValue(actualityExtraMetricDescriptors);
  const itemSetMetricDescriptorsMap = useItemSetMetricDescriptorsMap();
  const itemStatsMetricDescriptorsMap = useItemStatsMetricDescriptorsMap();
  const locationMetricDescriptorsMap = useLocationMetricDescriptorsMap();
  // const assignmentMetricDescriptorsMap = useAssignmentMetricDescriptorsMap();

  const metrics: ActualityMetricDescriptor[] = [
    ..._.values(itemSetMetricDescriptorsMap),
    ..._.values(itemStatsMetricDescriptorsMap),
    // ..._.values(assignmentMetricDescriptorsMap),
    ..._.values(locationMetricDescriptorsMap),
    // ..._.values(occupancyMetricDescriptorsMap).map(descriptor => ({
    //   ...descriptor,
    //   category: 'occupancy',
    //   titleCategory: t('Occupancy', { ns: 'dataset' }),
    //   title: t(descriptor.type, { ns: 'dataset' }),
    //   subTitle: t('Location', { ns: 'dataset' }),
    //   actionTitle: t('show occupancy heatmap', { ns: 'dataset' }),
    //   icon: Icon.HeatMap9,
    //   format: formatInteger,
    //   helpNavTo: `kpi/occupancy/${descriptor.type}`,
    // })),
    // TIME BASED METRICS
    {
      ...actualityMetricDescriptorsMap.lastActivity,
      titleCategory: t('Time', { ns: 'dataset' }),
      category: 'velocity',
      title: t('Activity (Latest)', { ns: 'dataset' }),
      subTitle: t('Item', { ns: 'dataset' }),
      actionTitle: t('show Visits heatmap', { ns: 'dataset' }),
      icon: Icon.HeatMap9,
      format: ms =>
        ms > 0 ? format(toLocalDateFromMs(ms * 1000), 'dd MMM, HH:mm') : '-',
      helpNavTo: 'kpi/time/last-activity',
    },

    // VELOCITY METRICS
    {
      ...actualityMetricDescriptorsMap.visits,
      category: 'velocity',
      titleCategory: t('Velocity', { ns: 'dataset' }),
      title: t('Visits (All)', { ns: 'dataset' }),
      subTitle: t('Item', { ns: 'dataset' }),
      actionTitle: t('show Visits heatmap', { ns: 'dataset' }),
      icon: Icon.HeatMap9,
      format: formatInteger,
      helpNavTo: 'kpi/velocity/visits',
    },
    {
      ...actualityMetricDescriptorsMap.distance,
      category: 'velocity',
      titleCategory: t('Velocity', { ns: 'dataset' }),
      title: t('Distance', { ns: 'dataset' }),
      subTitle: t('Item', { ns: 'dataset' }),
      actionTitle: t('show Visits heatmap', { ns: 'dataset' }),
      icon: Icon.HeatMap9,
      format: v => formatter.formatDistance(v).fullString,
      helpNavTo: 'kpi/velocity/visits',
    },
    {
      ...actualityMetricDescriptorsMap.distanceAverage,
      category: 'velocity',
      titleCategory: t('Velocity', { ns: 'dataset' }),
      title: t('Distance per Task', { ns: 'dataset' }),
      subTitle: t('Item', { ns: 'dataset' }),
      actionTitle: t('show Visits heatmap', { ns: 'dataset' }),
      icon: Icon.HeatMap9,
      format: v => formatter.formatDistance(v).fullString,
      helpNavTo: 'kpi/velocity/visits',
    },
    // {
    //   ...actualityMetricDescriptorsMap.visitsCurrent,
    //   titleCategory: t('Velocity', { ns: 'dataset' }),
    //   title: t('Visits', { ns: 'dataset' }),
    //   subTitle: t('Item', { ns: 'dataset' }),
    //   actionTitle: t('show Visits heatmap', { ns: 'dataset' }),
    //   icon: Icon.HeatMap9,
    //   format: formatInteger,
    // },
    {
      ...actualityMetricDescriptorsMap.visitsReplenishment,
      category: 'velocity',
      titleCategory: t('Velocity', { ns: 'dataset' }),
      title: t('Replenishments', { ns: 'dataset' }),
      subTitle: t('Item', { ns: 'dataset' }),
      actionTitle: t('show Visits heatmap', { ns: 'dataset' }),
      icon: Icon.HeatMap9,
      format: formatInteger,
      helpNavTo: 'kpi/velocity/replenishments',
    },
    {
      ...actualityMetricDescriptorsMap.avgReplenishmentDistance,
      category: 'velocity',
      titleCategory: t('Velocity', { ns: 'dataset' }),
      title: t('Replenishment Distance (AVG)', { ns: 'dataset' }),
      subTitle: t('Item', { ns: 'dataset' }),
      actionTitle: t('show Visits heatmap', { ns: 'dataset' }),
      icon: Icon.HeatMap9,
      format: v => formatter.formatDistance(v).fullString,
      helpNavTo: 'kpi/velocity/replenishments',
    },

    {
      ...actualityMetricDescriptorsMap.processTypeLatest,
      category: 'velocity',
      titleCategory: t('Time', { ns: 'dataset' }),
      title: t('Process (Latest)', { ns: 'dataset' }),
      subTitle: t('Item', { ns: 'dataset' }),
      actionTitle: t('show Visits heatmap', { ns: 'dataset' }),
      icon: Icon.HeatMap9,
      helpNavTo: 'kpi/time/process-type',
    },
    {
      ...actualityMetricDescriptorsMap.processType,
      category: 'velocity',
      titleCategory: t('Time', { ns: 'dataset' }),
      title: t('Process', { ns: 'dataset' }),
      subTitle: t('Item', { ns: 'dataset' }),
      actionTitle: t('show Visits heatmap', { ns: 'dataset' }),
      icon: Icon.HeatMap9,
      helpNavTo: 'kpi/time/process-type',
    },
    {
      ...actualityMetricDescriptorsMap.jobGroup,
      category: 'velocity',
      titleCategory: t('Time', { ns: 'dataset' }),
      title: t('Order', { ns: 'dataset' }),
      subTitle: t('Item', { ns: 'dataset' }),
      actionTitle: t('show Visits heatmap', { ns: 'dataset' }),
      icon: Icon.HeatMap9,
      helpNavTo: 'kpi/time/process-type',
    },
    {
      ...actualityMetricDescriptorsMap.sequenceValidation,
      category: 'compliance',
      titleCategory: t('Compliance', { ns: 'dataset' }),
      title: t('Out of Sequence', { ns: 'dataset' }),
      subTitle: t('Item', { ns: 'dataset' }),
      actionTitle: t('show sequence issues heatmap', { ns: 'dataset' }),
      icon: Icon.HeatMap9,
      helpNavTo: 'kpi/velocity/sequence-validation',
    },

    {
      ...actualityMetricDescriptorsMap.productLocationValidation,
      category: 'compliance',
      titleCategory: t('Compliance', { ns: 'dataset' }),
      title: t('Multi-Location Picks', { ns: 'dataset' }),
      subTitle: t('Item', { ns: 'dataset' }),
      actionTitle: t('show Multiple locations issues heatmap', {
        ns: 'dataset',
      }),
      icon: Icon.HeatMap9,
      helpNavTo: 'kpi/velocity/multi-location-picks',
    },
    {
      ...actualityMetricDescriptorsMap.totalUomQty,
      category: 'inventory',
      titleCategory: t('UOM', { ns: 'dataset' }),
      title: t('Stock UOM Quantity (SUM)', { ns: 'dataset' }),
      subTitle: t('Item', { ns: 'dataset' }),
      actionTitle: t('show Visits heatmap', { ns: 'dataset' }),
      icon: Icon.HeatMap9,
      format: formatInteger,
      helpNavTo: 'kpi/item-uom/total-qty',
    },
    {
      ...actualityMetricDescriptorsMap.avgUomQty,
      category: 'inventory',
      titleCategory: t('UOM', { ns: 'dataset' }),
      title: t('Stock UOM Quantity (AVG)', { ns: 'dataset' }),
      subTitle: t('Item', { ns: 'dataset' }),
      actionTitle: t('show Visits heatmap', { ns: 'dataset' }),
      icon: Icon.HeatMap9,
      format: formatInteger,
      helpNavTo: 'kpi/item-uom/avg-qty',
    },
    {
      ...actualityMetricDescriptorsMap.pickByAssignment,
      category: 'compliance',
      titleCategory: t('Compliance', { ns: 'dataset' }),
      title: t('Assignment Compliance', { ns: 'dataset' }),
      subTitle: t('Item', { ns: 'dataset' }),
      icon: Icon.CongestionMap4,
      helpNavTo: 'kpi/compliance/assignment',
    },
    // {
    //   ...actualityMetricDescriptorsMap.stockActivityTime,
    //   titleCategory: t('Inventory', { ns: 'dataset' }),
    //   title: t('Stock Duration', { ns: 'dataset' }),
    //   subTitle: t('Item', { ns: 'dataset' }),
    //   icon: Icon.CongestionMap4,
    // },

    // {
    //   ...actualityMetricDescriptorsMap.uomVolumePow,
    //   titleCategory: t('Item (UOM)', { ns: 'dataset' }),
    //   title: t('Base UOM Volume (Relative)', { ns: 'dataset' }),
    //   subTitle: t('Item', { ns: 'dataset' }),
    //   format: v => formatter.formatVolume(v).fullString,
    //   icon: Icon.CongestionMap4,
    // },
    // {
    //   ...actualityMetricDescriptorsMap.uomDensity,
    //   category: 'itemUom',
    //   titleCategory: t('Item (UOM)', { ns: 'dataset' }),
    //   title: t('UOM Density', { ns: 'dataset' }),
    //   subTitle: t('Item', { ns: 'dataset' }),
    //   format: v => formatter.formatInteger(v).fullString,
    //   icon: Icon.CongestionMap4,
    // },
    // {
    //   ...actualityMetricDescriptorsMap.uomCapacity,
    //   titleCategory: t('Item (UOM)', { ns: 'dataset' }),
    //   title: t('UOM Capacity', { ns: 'dataset' }),
    //   subTitle: t('Item', { ns: 'dataset' }),
    //   format: v => formatter.formatInteger(v).fullString,
    //   icon: Icon.CongestionMap4,
    // },
    // {
    //   ...actualityMetricDescriptorsMap.uomProportionality,
    //   category: 'itemUom',
    //   titleCategory: t('Item (UOM)', { ns: 'dataset' }),
    //   title: t('UOM Proportionality', { ns: 'dataset' }),
    //   subTitle: t('Item', { ns: 'dataset' }),
    //   format: v => formatter.formatInteger(v).fullString,
    //   icon: Icon.CongestionMap4,
    // },

    ...(extraDescriptors as any),
  ];

  return _.keyBy(metrics, d => d.type);
}

export default useActualityMetricDescriptorsMap;
